import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import innerStyles from './outline-accordion.inner.pcss?inline';
import globalStyles from './outline-accordion.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

import { OutlineAccordionItem } from '../outline-accordion-item/outline-accordion-item';

type AccordionItem = HTMLElementTagNameMap['outline-accordion-item'];

/**
 * The OutlineAccordion component
 * @element outline-accordion
 *
 * @extends LitElement
 *
 * @slot - The accordion's slot for outline-accordion-item.
 *
 * @property {boolean} singleOpen - Determines if multiple accordion items can be open at the same time. When set to true, only one item can be open at a time.
 * @property {boolean} firstOpen - Determines if first panel is open on page load.
 * @property {string} variant - The variant of the accordion.
 *
 * ## Documentation
 *
 * The `OutlineAccordion` component is a custom web component that extends `LitElement`. It is designed to contain multiple `outline-accordion-item` components and provides functionality to control their behavior.
 *
 * ### Properties
 * - `iconPosition` (string): Controls the position of the icon in the accordion item summary. Accepts 'start' or 'end'.
 * - `singleOpen` (boolean): When set to true, only one accordion item can be open at a time. If another item is opened, the previously opened item will close automatically.
 * - `variant` (string): The variant of the accordion.
 *
 * ### Slots
 * - Default slot: Used to place `outline-accordion-item` components inside the accordion.
 *
 * ### Events
 * - `accordion-item-click`: Dispatched when the summary of an `outline-accordion-item` is clicked.
 *
 * ### Methods
 * - `checkSlotContent()`: Checks the content of the slot and sets up the slotted accordion items.
 * - `handleAccordionItemClicked(event: Event)`: Manages the opening and closing of accordion items based on the `singleOpen` property.
 */

@customElement('outline-accordion')
export class OutlineAccordion extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  // The current slotted outline-accordion-items array or single outline-accordion-item
  private slottedAccordionItems: AccordionItem[] | null = null;
  private currentSelectedAccordion: AccordionItem | undefined | null = null;

  /**
   * Property to control if only a single accordion is allowed open at one time
   */
  @property({ reflect: true, type: Boolean, attribute: 'single-open' })
  singleOpen = false;

  /**
   *  Sets first panel to be open on page load.
   */
  @property({ reflect: true, type: Boolean, attribute: 'first-open' })
  firstOpen = false;

  /**
   * The variant of the accordion.
   */
  @property({ type: String, attribute: 'variation' })
  variant = 'default';

  firstUpdated(changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(changedProperties);
    this.checkSlotContent();
    this.handleFirstOpen();
  }

  checkSlotContent() {
    this.shadowRoot?.querySelectorAll('slot').forEach(slot => {
      const nodes = slot.assignedNodes({ flatten: true });
      this.slottedAccordionItems = nodes.filter(
        node => node.nodeName === 'OUTLINE-ACCORDION-ITEM'
      ) as AccordionItem[];
    });
  }

  // Listen for when the child accordion item's are ready
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(
      'accordion-item-click',
      this.handleAccordionItemClicked
    );
  }

  // Find the parent outline-accordion-item component that was clicked, filter that out, and close all other outline-accordion-item
  handleAccordionItemClicked(event: Event) {
    const path = event.composedPath();
    this.currentSelectedAccordion = path.find(
      p => p instanceof OutlineAccordionItem
    ) as OutlineAccordionItem | undefined;

    // If singleOpen is false or slottedAccordionItems is null eject
    if (!this.singleOpen || !this.slottedAccordionItems) {
      return;
    }

    const openAccordionItems = this.slottedAccordionItems.filter(item => {
      const details = item.shadowRoot?.querySelector('details');
      return (
        details?.hasAttribute('open') && item !== this.currentSelectedAccordion
      );
    });

    openAccordionItems.forEach(accordionItem => {
      if (typeof accordionItem.shrink === 'function') {
        accordionItem.shrink();
      } else {
        const currentDetails =
          accordionItem.shadowRoot?.querySelector('details');
        currentDetails?.removeAttribute('open');
      }
    });
  }

  handleFirstOpen() {
    if (this.slottedAccordionItems?.length && this.firstOpen) {
      const firstPanel = this.slottedAccordionItems[0];
      setTimeout(() => {
        const details = firstPanel?.shadowRoot?.querySelector('details');
        const iconWrapper = firstPanel?.shadowRoot?.querySelector('.icon-wrapper');

        // Set the open attribute on the first panel
        details?.setAttribute('open', '');

        // Rotate the icon 360°
        if (iconWrapper) {
          iconWrapper.animate(
            { transform: ['rotate(0deg)', 'rotate(180deg)'] },
            { duration: 0, fill: 'forwards' }
          );
        }
      }, 0);
    }
  }

  render(): TemplateResult {
    return html`
      <section class="outline--accordion">
        <div class="outline-accordion__accordion-items">
          <slot></slot>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-accordion': OutlineAccordion;
  }
}
